import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IExportResponse } from 'APIPackage/ImportExportModule/interfaces/IExportResponse';
import { deleteExport, fetchExportFileURL, fetchExports } from 'APIPackage/ImportExportModule/services/exportApi';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { exportDownloadFile, exportGetData, exportRemove, exportSetData } from '../actions/exportActions';
import { ExportActionsEnum } from '../enums/ExportActionsEnum';
import { getExportData, getExportOrder, getExportPage } from '../selectors/exportSelectors';
import { mapDataToExportRow } from '../common/mapDataToExportRow';

const PAGE_SIZE = 50;
const EXPORT_DATA_REFRESH_DELAY = 1500;

export function* watchExportSaga() {
  yield takeEvery(ExportActionsEnum.EXPORT_INIT_PAGE, exportInitPageSaga);
  yield takeEvery(ExportActionsEnum.EXPORT_GET_DATA, exportGetDataSaga);
  yield takeEvery(ExportActionsEnum.EXPORT_GET_DATA_NEXT_PAGE, exportGetDataNextPageSaga);
  yield takeEvery(ExportActionsEnum.EXPORT_REMOVE, exportRemoveSaga);
  yield takeEvery(ExportActionsEnum.EXPORT_DOWNLOAD_FILE, exportDownloadFileSaga);
}

function* exportInitPageSaga() {
  yield put(exportGetData());
}

function* exportGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getExportOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const res: IExportResponse = yield fetchExports(0, PAGE_SIZE, order ?? '');

    yield put(exportSetData(mapDataToExportRow(res.list, isErknm), res.page, res.pageCount, res.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* exportGetDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const data = getExportData(state);
    const page = getExportPage(state);
    const order = getExportOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const res: IExportResponse = yield fetchExports(page + 1, PAGE_SIZE, order ?? '');

    yield put(
      exportSetData([...data, ...mapDataToExportRow(res.list, isErknm)], res.page, res.pageCount, res.totalCount),
    );
  }
  yield sagaWrapper({
    funcTry,
  });
}

function* exportRemoveSaga(action: ActionType<typeof exportRemove>) {
  function* funcTry() {
    const { removedIds } = action.payload;

    yield removedIds.forEach(id => {
      deleteExport(id);
    });
    yield delay(EXPORT_DATA_REFRESH_DELAY);
    yield put(exportGetData());
    if (removedIds.length > 1) {
      const deletingQuantityText = num2str(removedIds.length, ['запись', 'записи', 'записей']);

      yield put(
        addNoticeAction({
          renderContent: `Успешно ${num2str(removedIds.length, ['удалена', 'удалено', 'удалено'])} ${
            removedIds.length
          } ${deletingQuantityText}`,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.REMOVE_NOTES,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: 'Успешно удалена 1 запись',
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.REMOVE_NOTES,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* exportDownloadFileSaga(action: ActionType<typeof exportDownloadFile>) {
  function* funcTry() {
    const { id, filename } = action.payload;
    const fileUrl: string = yield fetchExportFileURL(id);

    saveFile(fileUrl, filename);
  }
  yield sagaWrapper({
    funcTry,
  });
}
